/**
 * @fileoverview
 * Authentication Service - Manages user authentication
 * Uses Angular Signals for reactive state management
 *
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, Subject, tap, catchError, throwError } from 'rxjs';

import { environment } from 'projects/super-admin/src/environments/environment';
import { API_ENDPOINTS } from '../../constants/api-endpoints.const';
import { STORAGE_KEYS } from '../../constants/storage-keys.const';
import { StorageUtil } from '../../utils/storage.utils';
import { TokenService } from './token.service';
import {
  User,
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
} from '../models/super-admin.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  /** Used for refresh only — bypasses HTTP interceptors to avoid refresh recursion and DI cycles. */
  private readonly refreshHttp = new HttpClient(inject(HttpBackend));
  private readonly router = inject(Router);
  private readonly tokenService = inject(TokenService);

  /** Coalesces concurrent refresh calls (inactivity “stay logged in”, 401 retry, init restore). */
  private refreshCoalesced$: Observable<RefreshTokenResponse> | null = null;

  private logoutRequestStarted = false;

  // Signals for reactive state
  private readonly userSignal = signal<User | null>(this.loadUserFromStorage());
  private readonly loadingSignal = signal<boolean>(false);
  private readonly errorSignal = signal<string | null>(null);

  // Public readonly signals
  readonly user = this.userSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();

  // Computed signals
  readonly isAuthenticated = computed(() => !!this.userSignal());
  readonly userName = computed(() => {
    const user = this.userSignal();
    if (!user) return '';
    return (
      user.display_name?.trim() ??
      [user.first_name, user.last_name].filter(Boolean).join(' ').trim() ??
      user.email
    );
  });
  readonly userFullName = computed(() => {
    const user = this.userSignal();
    if (!user) return '';
    const parts = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
    return parts ?? user.display_name?.trim() ?? user.email;
  });
  readonly userRole = computed(() => {
    const user = this.userSignal();
    if (!user) return '';
    if (typeof user.role === 'string' && user.role.length > 0) {
      return user.role;
    }
    return user.account_type ?? '';
  });
  
  readonly accountTypeLabel = computed(() =>
    this.formatAccountTypeLabel(this.userRole())
  );

  initAuthState(): void {
    this.checkAuthentication();
  }

  /**
   * Login with email and password
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    return this.http
      .post<LoginResponse>(
        `${environment.apiUrl}${API_ENDPOINTS.AUTH.LOGIN}`,
        credentials
      )
      .pipe(
        tap((response) => {
          this.handleLoginSuccess(response);
          this.loadingSignal.set(false);
        }),
        catchError((error) => {
          this.handleLoginError(error);
          this.loadingSignal.set(false);
          return throwError(() => error);
        })
      );
  }

  /**
   * Logout current user
   */
  logout(): void {
    if (this.logoutRequestStarted) {
      return;
    }
    console.info('[Auth] logout API call');
    this.logoutRequestStarted = true;
    this.loadingSignal.set(true);

    const refreshToken = this.tokenService.getRefreshToken() ?? '';

    this.http
      .post(`${environment.apiUrl}${API_ENDPOINTS.AUTH.LOGOUT}`, { refreshToken })
      .subscribe({
        complete: () => {
          this.logoutRequestStarted = false;
          this.handleLogout();
        },
        error: () => {
          this.logoutRequestStarted = false;
          this.handleLogout();
        },
      });
  }

  /**
   * Refresh access token
   */
  refreshToken(): Observable<RefreshTokenResponse> {
    if (this.refreshCoalesced$) {
      return this.refreshCoalesced$;
    }

    const refreshToken = this.tokenService.getRefreshToken();

    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    const request: RefreshTokenRequest = { refreshToken };
    const bridge = new Subject<RefreshTokenResponse>();
    this.refreshCoalesced$ = bridge.asObservable();

    console.info('[Auth] refresh token API call');

    this.refreshHttp
      .post<RefreshTokenResponse>(
        `${environment.apiUrl}${API_ENDPOINTS.AUTH.REFRESH}`,
        request
      )
      .subscribe({
        next: (response) => {
          this.tokenService.setToken(response.data?.accessToken);
          if (response.data?.refreshToken) {
            this.tokenService.setRefreshToken(response.data.refreshToken);
          }
          bridge.next(response);
          bridge.complete();
          this.refreshCoalesced$ = null;
        },
        error: (error: unknown) => {
          this.handleLogout();
          bridge.error(error);
          this.refreshCoalesced$ = null;
        },
      });

    return this.refreshCoalesced$;
  }

  /**
   * Forgot password request
   */
  forgotPassword(request: ForgotPasswordRequest): Observable<{ message: string }> {
    this.loadingSignal.set(true);

    return this.http
      .post<{ message: string }>(
        `${environment.apiUrl}${API_ENDPOINTS.AUTH.FORGOT_PASSWORD}`,
        request
      )
      .pipe(
        tap(() => this.loadingSignal.set(false)),
        catchError((error) => {
          this.loadingSignal.set(false);
          return throwError(() => error);
        })
      );
  }

  /**
   * Reset password with token
   */
  resetPassword(request: ResetPasswordRequest): Observable<{ message: string }> {
    this.loadingSignal.set(true);

    return this.http
      .post<{ message: string }>(
        `${environment.apiUrl}${API_ENDPOINTS.AUTH.RESET_PASSWORD}`,
        request
      )
      .pipe(
        tap(() => this.loadingSignal.set(false)),
        catchError((error) => {
          this.loadingSignal.set(false);
          return throwError(() => error);
        })
      );
  }

  /**
   * Change password for the authenticated user
   */
  changePassword(request: ChangePasswordRequest): Observable<{ message: string }> {
    this.loadingSignal.set(true);

    return this.http
      .post<{ message: string }>(
        `${environment.apiUrl}${API_ENDPOINTS.AUTH.CHANGE_PASSWORD}`,
        request
      )
      .pipe(
        tap(() => this.loadingSignal.set(false)),
        catchError((error) => {
          this.loadingSignal.set(false);
          return throwError(() => error);
        })
      );
  }

  /**
   * Get current user from backend
   */
  getCurrentUser(): Observable<User> {
    return this.http
      .get<User>(`${environment.apiUrl}${API_ENDPOINTS.AUTH.ME}`)
      .pipe(
        tap((user) => {
          this.userSignal.set(user);
          this.saveUserToStorage(user);
        })
      );
  }

  /** Updates session user after login or profile edits. */
  setSessionUser(user: User): void {
    this.userSignal.set(user);
    this.saveUserToStorage(user);
  }

  /**
   * Handle successful login
   */
  private handleLoginSuccess(response: LoginResponse): void {
    this.tokenService.setToken(response.data.accessToken);
    this.tokenService.setRefreshToken(response.data.refreshToken);

    this.userSignal.set(response.data.user);
    this.saveUserToStorage(response.data.user);

    this.errorSignal.set(null);
  }

  /**
   * Handle login error
   */
  private handleLoginError(error: any): void {
    const raw =
      error.errorMessage ||
      error.error?.message ||
      (Array.isArray(error.error?.message) ? error.error.message.join(', ') : null) ||
      'Login failed. Please try again.';
    const errorMessage = typeof raw === 'string' ? raw : String(raw);
    this.errorSignal.set(errorMessage);
  }

  /**
   * Clears persisted auth without calling the logout API or navigating.
   * Used by route guards when the session cannot be restored.
   */
  discardLocalSession(): void {
    this.tokenService.removeTokens();
    this.userSignal.set(null);
    StorageUtil.removeLocal(STORAGE_KEYS.USER_DATA);
    this.errorSignal.set(null);
  }

  /**
   * Handle logout
   */
  private handleLogout(): void {
    this.discardLocalSession();
    this.loadingSignal.set(false);
    this.router.navigate(['/auth/login']);
  }

  /**
   * Save user to storage
   */
  private saveUserToStorage(user: User): void {
    StorageUtil.setLocal(STORAGE_KEYS.USER_DATA, user);
  }

  /**
   * Load user from storage
   */
  private loadUserFromStorage(): User | null {
    return StorageUtil.getLocal<User>(STORAGE_KEYS.USER_DATA);
  }

  private formatAccountTypeLabel(value: string): string {
    if (!value) return '';
    return value
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Check authentication status on app init — restores session from stored token.
   * Navigation is left to route guards.
   */
  private checkAuthentication(): void {
    const hasToken = this.tokenService.hasToken();
    const isExpired = this.tokenService.isTokenExpired();

    if (hasToken && !isExpired) {
      const user = this.loadUserFromStorage();
      if (user) {
        this.userSignal.set(user);
      } else {
        this.getCurrentUser().subscribe({
          error: () => this.handleLogout(),
        });
      }
    } else if (hasToken && isExpired) {
      this.refreshToken().subscribe({
        next: () => {
          this.getCurrentUser().subscribe();
        },
        error: () => this.handleLogout(),
      });
    } else {
      this.userSignal.set(null);
    }
  }
}
